import { useState, useEffect } from 'react';
import { api, type Asset, type Source, type Suggestion, type Turn, type TalentRow, type Sourcing } from '../lib/api';

// ---------------------------------------------------------------------------
// The assistant director: it proposes, the creator decides.
//
// Every proposal is a separate yes or no. That is the whole design, and it is
// the opposite of the shape the script generator has — which hands over one
// object, whole, and leaves the creator to edit back whatever they disliked.
// A director working that way makes the creator's cut something they have to
// defend. Working this way it stays something they own.
//
// Denied proposals stay on screen, greyed. They are the record of what was
// decided, which is what a rollback reads — and a denial that simply vanishes
// takes with it the only evidence the assistant ever suggested it.
// ---------------------------------------------------------------------------

function mmss(s: number): string {
  const whole = Math.max(0, Math.round(s));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function Director({ cardId, rundown, lineSeconds, suggestions, sources, assets = [], brandId = null, onChanged, onDress, onAssembled, episodeGathered = false, assembleAsk = 0, sourcing = 'library' }: {
  cardId: string;
  /** 190: where the director may draw from; a `collection` card cannot range wider. */
  sourcing?: Sourcing;
  /** D2: dress the episode in the house style in one move. */
  onDress?: () => void;
  /** P2: after Assemble — the card moves on to Editing. */
  onAssembled?: () => void | Promise<void>;
  /** Whether this episode has gathered anything (093); Assemble draws from it. */
  episodeGathered?: boolean;
  /** P8: the phase bar asks for an assembly by counting up. */
  assembleAsk?: number;
  rundown: Turn[];
  lineSeconds: number[];
  suggestions: Suggestion[];
  /** So a cut can be shown as the clip's NAME rather than as a uuid. */
  sources: Source[];
  /** So a bed can be shown as the track's name. */
  assets?: Asset[];
  /** Where a generated picture is filed. */
  brandId?: string | null;
  onChanged: () => void | Promise<void>;
}) {
  const [brief, setBrief] = useState('');
  const [wide, setWide] = useState(false);
  const [make, setMake] = useState('');
  // 191: who is in the picture. Their sheets go in the prompt and their
  // portraits go in as references, so Niko is Niko in every scene.
  const [talent, setTalent] = useState<TalentRow[]>([]);
  const [inPicture, setInPicture] = useState<string[]>([]);
  useEffect(() => { let live = true; api.talent().then((r) => { if (live) setTalent(r.talent); }).catch(() => {}); return () => { live = false; }; }, []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Where each scene starts, so a proposal anchored to "scene 3 plus one
  // second" can be shown as a time. The anchor is what is STORED — this is
  // only a reading of where that lands today, and it moves when the script
  // does, which is the entire reason layers anchor to scenes.
  const startOf = new Map<string, number>();
  let at = 0;
  rundown.forEach((t, i) => {
    if (t.id) startOf.set(t.id, at);
    at += lineSeconds[i] ?? 0;
  });

  /** P2: the whole assembly, so the button and the phase bar do the same thing. */
  async function assemble() {
    await run('assemble', async () => {
      const r = await api.directCard(cardId, brief.trim() || undefined, wide && sourcing === 'library' ? 'vault' : episodeGathered ? 'episode' : 'shelf');
      const pending = r.suggestions.filter((s) => s.status === 'pending');
      for (const sg of pending) {
        try { await api.decideSuggestion(cardId, sg.id, 'approve'); } catch { /* one refused proposal is not a failed assembly */ }
      }
      onDress?.();
      await onAssembled?.();
      setNote(pending.length ? `Assembled: ${pending.length} placed from ${episodeGathered ? "this episode's collection" : 'the shelf'}, dressed, and moved to Editing.` : (r.detail ?? 'nothing to place — gather footage for this episode first'));
    });
  }
  useEffect(() => { if (assembleAsk > 0) void assemble(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [assembleAsk]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null); setNote(null);
    try { await fn(); await onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'that did not work'); }
    finally { setBusy(null); }
  }

  const pending = suggestions.filter((s) => s.status === 'pending');
  const decided = suggestions.filter((s) => s.status !== 'pending');

  return (
    <div className="director">
      <div className="row between">
        <h4>Assistant director{pending.length ? <span className="chip"> {pending.length} to decide</span> : null}</h4>
        <span className="row">
          {pending.length > 1 && (
            // One click for "none of these", because deciding fourteen
            // proposals one at a time when the answer to all of them is no is
            // the kind of chore that makes a creator stop asking.
            <button className="btn" disabled={Boolean(busy)}
                    onClick={() => void run('denyall', () => api.denyAllSuggestions(cardId))}>
              Deny all
            </button>
          )}
          <button className="btn" disabled={Boolean(busy) || rundown.length === 0}
                  onClick={() => void run('direct', async () => {
                    const r = await api.directCard(cardId, brief.trim() || undefined,
                                                   wide ? 'vault' : 'shelf');
                    if (!r.suggestions.length) setNote(r.detail ?? 'nothing to add');
                  })}>
            {busy === 'direct' ? 'Thinking…' : 'Suggest'}
          </button>
        </span>
      </div>

      <input
        className="brieffield"
        value={brief}
        placeholder="Optional — what should it look for? e.g. name the speakers, add a subscribe prompt"
        onChange={(e) => setBrief(e.target.value)}
      />

      {/* The shelf, in one line. By default the director reaches only for clips
          the creator has approved; this opens the whole library for one
          suggestion. Approving a vault clip puts it on the shelf, so the
          working set gets built by working. */}
      <label className="director-scope" title={sourcing === 'collection' ? 'This episode draws only from its collection' : sourcing === 'fresh' ? 'This episode starts from scratch — no clips' : undefined}>
        <input type="checkbox" checked={wide && sourcing === 'library'} disabled={sourcing !== 'library'} onChange={(e) => setWide(e.target.checked)} />
        Range over every clip, not just the shelf
      </label>

      {/* Make the B-roll that is not in the library. The director proposes only
          from what the creator owns — it never invents a clip — so when what
          the piece needs does not exist yet, this is how it comes to exist:
          described in words, generated, and dropped into the library to be
          dragged on or proposed next time. */}
      <form className="row director-make" onSubmit={(e) => {
        e.preventDefault();
        const prompt = make.trim();
        if (prompt.length < 5) return;
        void run('make', async () => {
          // 190: into this episode's collection, so the next Assemble can reach it.
          await api.generateAsset(prompt, brandId, { cardId, ...(inPicture.length ? { talent: inPicture } : {}) });
          setMake('');
          setNote('Making it — it will be in this episode\'s collection in a few minutes, once the render plane has looked at it.');
        });
      }}>
        <input value={make} onChange={(e) => setMake(e.target.value)} maxLength={500}
               placeholder="Or make a picture: six egrets in a mangrove at dawn" />
        {talent.length > 0 && (
          <span className="row wrap" style={{ gap: 6, alignItems: 'center' }} title="191: who is in the picture — drawn from their sheet and portrait">
            <span className="sub-dim">with</span>
            {talent.map((t) => (
              <label key={t.id} className="chip" style={{ cursor: 'pointer', opacity: inPicture.includes(t.id) ? 1 : 0.7 }}>
                <input type="checkbox" checked={inPicture.includes(t.id)} style={{ marginRight: 4 }}
                       disabled={!inPicture.includes(t.id) && inPicture.length >= 3}
                       onChange={(e) => setInPicture((cur) => e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id))} />
                {t.name}
              </label>
            ))}
          </span>
        )}
        <button className="btn pri" type="button" disabled={Boolean(busy) || rundown.length === 0}
                title="P2: the director lays the episode out from what it gathered — cuts, layers, the bed — approves the lot, dresses it in the house style, and moves the card on to Editing. Everything stays adjustable on the timeline."
                onClick={() => void assemble()}>
          {busy === 'assemble' ? 'Assembling…' : 'Assemble the episode'}
        </button>
        {onDress && (
          <button className="btn" type="button" disabled={Boolean(busy)} onClick={onDress}
                  title="The show open after the cold open, the mark for the whole programme, the social bar and SUBSCRIBE at the end — whatever is not there yet">
            Dress the episode
          </button>
        )}
        <button className="btn" type="submit" disabled={Boolean(busy) || make.trim().length < 5}>
          {busy === 'make' ? 'Making…' : 'Make'}
        </button>
      </form>

      {error && <p className="director-error">{error}</p>}
      {note && <p className="hint">{note}</p>}

      {pending.length === 0 && decided.length === 0 && !busy && (
        <p className="hint">
          Nothing proposed yet. The director places captions and banners against
          your scenes — it never rewrites the script.
        </p>
      )}

      {pending.map((s) => {
        const cut = s.kind === 'source';
        const bed = s.kind === 'music';
        const clip = cut ? sources.find((x) => x.id === s.payload.sourceId) : undefined;
        const track = bed ? assets.find((a) => a.id === s.payload.assetId) : undefined;
        const start = (startOf.get(s.payload.anchorId ?? '') ?? 0) + (s.payload.offsetSeconds ?? 0);
        // A proposal whose scene has been edited away. Approving it would fail
        // at the API, so say so here rather than offering a button that cannot
        // work. A bed has no scene, so it cannot be orphaned that way.
        const orphan = !bed && !startOf.has(s.payload.anchorId ?? '');
        // A clip that has been deleted since the proposal. Same treatment as an
        // orphaned anchor: the API would refuse it, so do not offer the button.
        const lostClip = cut && !clip;
        const lostTrack = bed && assets.length > 0 && !track;
        const blocked = orphan || lostClip || lostTrack;
        const sceneNo = rundown.findIndex((t) => t.id === s.payload.anchorId) + 1;
        return (
          <div className={`sugg${blocked ? ' is-orphan' : ''}${cut ? ' is-cut' : ''}${bed ? ' is-bed' : ''}`} key={s.id}>
            <div className="sugg-what">
              <strong>
                {bed ? (track?.name?.trim() || track?.storage_key.split('/').pop() || 'a track that is no longer here')
                  : cut ? (clip?.name ?? 'a clip that is no longer here')
                  : s.payload.props?.text}
              </strong>
              <span className="sugg-where">
                {orphan ? 'the scene this was placed on is gone'
                  : lostClip ? 'that clip is no longer in the library'
                  : lostTrack ? 'that audio is no longer in the library'
                  : bed ? `music bed · under everything${s.payload.gainDb !== undefined ? ` · ${s.payload.gainDb}dB` : ''}${s.payload.duck === false ? ' · no ducking' : ''}`
                  : cut ? `cut scene ${sceneNo} · ${mmss(start)}`
                  : `${s.payload.props?.style ?? 'banner'} · ${s.payload.props?.position ?? 'bottom'} · ${mmss(start)} for ${(s.payload.seconds ?? 0).toFixed(1)}s${s.payload.props?.sub ? ` · “${s.payload.props.sub}”` : ''}${s.payload.props?.url ? ' · QR' : ''}`}
              </span>
            </div>
            {cut && !blocked && (
              /* Said plainly, because this one is different in kind: approving
                 it EDITS the script rather than adding something beside it. */
              <p className="sugg-edit">
                Approving changes the script — scene {sceneNo} will cut to this
                clip instead of drawing a panel.
                {s.drawn_from === 'vault' && ' It also puts the clip on your shelf.'}
              </p>
            )}
            <p className="sugg-why">{s.rationale}</p>
            <div className="row">
              <button className="btn pri" disabled={Boolean(busy) || blocked}
                      onClick={() => void run(s.id, () => api.decideSuggestion(cardId, s.id, 'approve'))}>
                Approve
              </button>
              <button className="btn" disabled={Boolean(busy)}
                      onClick={() => void run(s.id, () => api.decideSuggestion(cardId, s.id, 'deny'))}>
                Deny
              </button>
              {/* Not this, but something here. The director is told what was
                  refused and why, so it does not offer the same thing in
                  different words — which is what a model does when asked the
                  same question twice with nothing new to go on. */}
              <button className="btn" disabled={Boolean(busy)} title="Deny this and ask for a different one for the same spot"
                      onClick={() => void run(s.id, async () => {
                        const r = await api.askAgain(cardId, s.id);
                        if (!r.suggestions.length) setNote(r.detail ?? 'the director had nothing else to offer');
                      })}>
                Ask again
              </button>
            </div>
          </div>
        );
      })}

      {decided.length > 0 && (
        <details className="sugg-history">
          <summary>{decided.length} already decided</summary>
          {decided.map((s) => (
            <div className={`sugg is-${s.status}`} key={s.id}>
              <div className="sugg-what">
                <strong>
                  {s.kind === 'source'
                    ? (sources.find((x) => x.id === s.payload.sourceId)?.name ?? 'a clip')
                    : s.payload.props?.text}
                </strong>
                <span className="sugg-where">{s.status}</span>
              </div>
              <p className="sugg-why">{s.rationale}</p>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
